// The header bell: unread badge + dropdown preview of recent notifications.
// Presentational shell over the notification slice hooks.

import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NOTIFICATIONS_ROUTE } from "./routes.js";
import { useNotifications } from "./useNotifications.js";
import { useNotificationsContext } from "./NotificationsProvider.js";
import NotificationRow from "./NotificationRow.js";
import { isUnread } from "./model.js";

const DROPDOWN_PAGE_SIZE = 8;

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["features", "common"]);
  const { unreadCount, refreshUnreadCount } = useNotificationsContext();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { items, isLoading, error, markRead, markAllRead, refresh } =
    useNotifications({
      pageSize: DROPDOWN_PAGE_SIZE,
      enabled: isOpen,
    });

  // Refresh the badge + list whenever the dropdown opens.
  useEffect(() => {
    if (!isOpen) return;
    void refreshUnreadCount();
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  // The dropdown is an unread-only preview: reading/dismissing removes an item
  // from here; the full history stays available on the notifications screen.
  const preview = items.filter(isUnread).slice(0, DROPDOWN_PAGE_SIZE);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="relative flex items-center justify-center h-10 w-10 rounded-xl text-golden hover:bg-golden/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-golden"
        aria-label={
          unreadCount > 0
            ? t("notifications.bellAriaUnread", { count: unreadCount })
            : t("notifications.title")
        }
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <Bell className="h-6 w-6 translate-x-[0.5px]" aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
            aria-hidden="true"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: dim backdrop behind the centered modal. */}
          <div
            className="fixed inset-0 z-[99999] bg-black/30 md:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed left-1/2 top-16 sm:top-20 z-[100000] mt-2 w-[92vw] max-w-sm -translate-x-1/2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden md:absolute md:left-auto md:right-0 md:top-full md:w-[28rem] md:max-w-none md:translate-x-0"
            role="menu"
            aria-label={t("notifications.title")}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-bold text-my-black">{t("notifications.title")}</p>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  className="flex items-center justify-center h-8 w-8 rounded-lg text-main-purple hover:bg-main-purple/10 transition-colors focus:outline-none focus:ring-2 focus:ring-golden"
                  aria-label={t("notifications.markAllRead")}
                  title={t("notifications.markAllRead")}
                >
                  <CheckCheck className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>

            <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-50">
              {isLoading && preview.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-gray-400">
                  {t("common:status.loading")}
                </p>
              ) : error ? (
                <p className="px-4 py-8 text-center text-sm text-gray-400">
                  {t("notifications.loadError")}
                </p>
              ) : preview.length === 0 ? (
                <div className="px-4 py-10 flex flex-col items-center text-center">
                  <Bell className="h-8 w-8 text-gray-300" aria-hidden="true" />
                  <p className="mt-2 text-sm text-gray-500">
                    {t("notifications.noUnread")}
                  </p>
                </div>
              ) : (
                preview.map((item) => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    onMarkRead={markRead}
                    actions="dismiss"
                    onNavigate={() => setIsOpen(false)}
                    compact
                  />
                ))
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate(NOTIFICATIONS_ROUTE);
              }}
              className="w-full px-4 py-3 text-center text-sm font-semibold text-main-purple border-t border-gray-100 hover:bg-golden/10 transition-colors focus:outline-none"
            >
              {t("notifications.seeAll")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
